export const buildChartData = (econData, comData, companyData, fxData, cryptoData, selectedItems) => {
    const datasets = [];
  
    // Economics data
    for (const [key, val] of Object.entries(econData)) {
      if (val.data) {
        datasets.push({
          label: key,
          data: val.data.map(d => ({ x: d.date, y: d.value })),
          borderColor: getRandomColor(),
          fill: false,
          pointRadius: 0,
          yAxisID: `y-${key}`
        });
      }
    }
  
    // Commodity data
    const comMap = {};
    comData.data?.forEach(row => {
      for (const key in row) {
        if (key !== 'date') {
          if (!comMap[key]) comMap[key] = [];
          comMap[key].push({ x: row.date, y: parseFloat(row[key]) });
        }
      }
    });
  
    for (const [key, points] of Object.entries(comMap)) {
      datasets.push({
        label: key,
        data: points,
        borderColor: getRandomColor(),
        fill: false,
        pointRadius: 0,
        yAxisID: `y-${key}`
      });
    }

    // Company data
    for (const [symbol, stockData] of Object.entries(companyData)) {
      if (stockData && stockData['Time Series (Daily)']) {
        const timeSeries = stockData['Time Series (Daily)'];
        const stockPoints = Object.entries(timeSeries)
          .map(([date, values]) => ({
            x: date,
            y: parseFloat(values['5. adjusted close'])
          }))
          .sort((a, b) => new Date(a.x) - new Date(b.x)); // Sort by date ascending

        // Find the company name from selectedItems
        const companyItem = selectedItems?.find(item => item.type === 'company' && item.code === symbol);
        const displayName = companyItem ? companyItem.name : `Ticker: ${symbol}`;

        datasets.push({
          label: `${displayName} (Stock Price)`,
          data: stockPoints,
          borderColor: getRandomColor(),
          fill: false,
          pointRadius: 0,
          yAxisID: `y-${symbol}`
        });
      }
    }

    // Foreign Exchange data
    for (const [pair, fxPairData] of Object.entries(fxData)) {
      if (fxPairData && fxPairData['Time Series FX (Daily)']) {
        const timeSeries = fxPairData['Time Series FX (Daily)'];
        const fxPoints = Object.entries(timeSeries)
          .map(([date, values]) => ({
            x: date,
            y: parseFloat(values['4. close'])
          }))
          .sort((a, b) => new Date(a.x) - new Date(b.x)); // Sort by date ascending

        // Find the FX name from selectedItems
        const fxItem = selectedItems?.find(item => item.type === 'fx' && item.code === pair);
        const displayName = fxItem ? fxItem.name : pair;

        datasets.push({
          label: `${displayName} (Exchange Rate)`,
          data: fxPoints,
          borderColor: getRandomColor(),
          fill: false,
          pointRadius: 0,
          yAxisID: `y-${pair}`
        });
      }
    }

    // Cryptocurrency data
    for (const [pair, cryptoPairData] of Object.entries(cryptoData)) {
      if (cryptoPairData && cryptoPairData['Time Series (Digital Currency Daily)']) {
        const timeSeries = cryptoPairData['Time Series (Digital Currency Daily)'];
        
        const cryptoPoints = Object.entries(timeSeries)
          .map(([date, values]) => ({
            x: date,
            y: parseFloat(values['4. close']) // Use the correct key from Alpha Vantage
          }))
          .sort((a, b) => new Date(a.x) - new Date(b.x)); // Sort by date ascending

        // Find the crypto name from selectedItems
        const cryptoItem = selectedItems?.find(item => item.type === 'crypto' && item.code === pair);
        const displayName = cryptoItem ? cryptoItem.name : pair;

        datasets.push({
          label: `${displayName} (Crypto Price)`,
          data: cryptoPoints,
          borderColor: getRandomColor(),
          fill: false,
          pointRadius: 0,
          yAxisID: `y-${pair}`
        });
      }
    }
  
    return datasets;
  };
  
  const getRandomColor = () => {
    // Generate softer, more friendly colors with good visibility
    const hue = Math.floor(Math.random() * 360); // Full hue range (0-360)
    const saturation = Math.floor(Math.random() * 25) + 50; // 50-75% saturation (softer, less harsh)
    const lightness = Math.floor(Math.random() * 25) + 40; // 40-65% lightness (warmer, friendlier)
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  export const buildYAxes = (econData, comData, companyData, fxData, cryptoData) => {
    const axes = {};
  
    // Economics data
    for (const key of Object.keys(econData)) {
      axes[`y-${key}`] = {
        type: 'linear',
        display: false,
        position: 'left'
      };
    }
  
    // Commodity data
    if (comData.data && comData.data.length > 0) {
      const firstRow = comData.data[0];
      for (const key in firstRow) {
        if (key !== 'date') {
          axes[`y-${key}`] = {
            type: 'linear',
            display: false,
            position: 'left'
          };
        }
      }
    }

    // Company data - add axis for each company ticker
    for (const symbol of Object.keys(companyData)) {
      axes[`y-${symbol}`] = {
        type: 'linear',
        display: false,
        position: 'left'
      };
    }

    // Foreign Exchange data - add axis for each FX pair
    for (const pair of Object.keys(fxData)) {
      axes[`y-${pair}`] = {
        type: 'linear',
        display: false,
        position: 'left'
      };
    }

    // Cryptocurrency data - add axis for each crypto pair
    for (const pair of Object.keys(cryptoData)) {
      axes[`y-${pair}`] = {
        type: 'linear',
        display: false,
        position: 'left'
      };
    }
  
    return axes;
  };
  